import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

// Simple, high-attention Zelle dialog with 3 rows x 2 columns layout.
// Left column: concise step text. Right column: the value (QR, Amount, Code).
// No phone mockups. Large fonts for amount and code. Copy helpers included.
//
// Props:
// - open: boolean
// - onClose: function (will be ignored if blockClose is true)
// - amount: number (e.g., 123.45)
// - refCode: string (e.g., '123456')
// - qrSrc: string (URL to QR image)
// - blockClose: boolean (default true) to prevent accidental dismiss
// - onCancel: function () => void
// - onConfirmSent: function () => void  (user says "I have sent the payment")
// - zelleAddress: optional string to display under QR (e.g., temple@...)
// - errorMessage: string (when status is failed) – show error and "Try different method" / "Retry Zelle"
// - onTryDifferentMethod: function () => void
// - onRetryZelle: function () => void
//
export default function ZelleDialogNew({
  open,
  onClose,
  amount = 0,
  refCode = '',
  qrSrc = '',
  blockClose = true,
  onCancel,
  onConfirmSent,
  zelleAddress,
  waiting,
  confirmed,
  warning,
  remainingMs = 0,
  onExtend,
  isGrace,
  canExtend = false,
  errorMessage,
  onTryDifferentMethod,
  onRetryZelle,
}) {
  // Audio-guided narration state
  const [activeStep, setActiveStep] = useState(null); // 1 | 2 | 3 | null
  const [muted, setMuted] = useState(false);
  const synthRef = useRef(null);
  const [voice, setVoice] = useState(null); // fallback single voice
  const [voices, setVoices] = useState([]); // up to 3 options to display
  const [voiceIdx, setVoiceIdx] = useState(0);
  const prettyAmount = useMemo(() => {
    const n = parseFloat(amount);
    if (!Number.isFinite(n)) return '$0.00';
    return `$${n.toFixed(2)}`;
  }, [amount]);

  const fmtTime = useCallback((ms) => {
    const s = Math.max(0, Math.round(ms / 1000));
    const m = Math.floor(s / 60);
    const ss = String(s % 60).padStart(2, '0');
    return `${m}:${ss}`;
  }, []);

  const safeClose = (evt, reason) => {
    if (blockClose) return; // block backdrop click/escape
    onClose?.(evt, reason);
  };

  // Speech helpers
  const cancelSpeech = useCallback(() => {
    try {
      const s = synthRef.current || window.speechSynthesis;
      if (s && s.speaking) s.cancel();
    } catch {}
  }, []);

  const speak = useCallback((text, onend) => {
    try {
      if (muted) return onend && onend();
      const synth = window.speechSynthesis;
      if (!synth) return onend && onend();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.88; // ~10% faster than previous 0.8
      u.pitch = 1.05;
      const chosen = (voices && voices[voiceIdx]) || voice;
      if (chosen) u.voice = chosen;
      u.onend = () => onend && onend();
      synth.speak(u);
      synthRef.current = synth;
    } catch {}
  }, [muted, voice, voices, voiceIdx]);

  // Pick and present up to 3 voices (prefer Indian/female), select a default
  useEffect(() => {
    try {
      const pickVoice = () => {
        const synth = window.speechSynthesis;
        if (!synth) return;
        const voices = synth.getVoices?.() || [];
        if (!voices.length) return;
        const score = (vv) => {
          const lang = (vv.lang || '').toLowerCase();
          const name = (vv.name || '').toLowerCase();
          let s = 0;
          // Prefer Asian English locales
          const asianEnLocales = ['en-in','en-sg','en-ph','en-hk','en-my','en-pk','en-bd'];
          if (asianEnLocales.some(l => lang.startsWith(l))) s += 100;
          // Names hinting Asian voices
          if (/(india|indian|hindi)/.test(name)) s += 60;
          // Prefer female tone slightly
          if (/female|woman|lady/.test(name)) s += 30;
          // Generic English fallback
          if (lang.startsWith('en')) s += 10;
          return s;
        };
        const sorted = [...voices].sort((a,b) => score(b) - score(a));
        const candidates = sorted.slice(0, 3);
        setVoices(candidates);
        // Pick the best-scoring candidate by default
        const best = candidates[0] || sorted[0] || null;
        setVoice(best);
        setVoiceIdx(0);
      };
      pickVoice();
      // Some browsers load voices asynchronously
      window.speechSynthesis?.addEventListener('voiceschanged', pickVoice, { once: true });
      return () => window.speechSynthesis?.removeEventListener('voiceschanged', pickVoice);
    } catch {}
  }, []);
  const runNarration = useCallback(() => {
    if (!open || muted) return;
    // Opening line
    speak('Follow three quick steps.', () => {
      // Step 1
      setActiveStep(1);
      speak('Open your bank app. Choose Zelle. Scan the QR code on this screen.', () => {
        // Step 2
        setActiveStep(2);
        const amt = (prettyAmount || '').replace('$', '');
        speak(`Enter the exact amount: ${amt}. `, () => {
          // Step 3
          setActiveStep(3);
          const code = String(refCode || '').split('').join(' ');
          speak(`In the memo or note, type this code exactly: ${code}.`, () => {
            // Final guidance
            speak('After sending, stay on this screen while we check the payment. Tap Play Instructions to hear again.', () => {
              setActiveStep(null);
            });
          });
        });
      });
    });
  }, [open, muted, speak, prettyAmount, refCode]);

  // Disable automatic narration; play only when user clicks the button
  useEffect(() => {
    // no-op on open
    return () => {};
  }, [open, muted, runNarration]);

  // Stop narration when dialog closes or on unmount
  useEffect(() => {
    if (!open) cancelSpeech();
    return () => cancelSpeech();
  }, [open, cancelSpeech]);

  return (
    <Dialog
      open={!!open}
      onClose={safeClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="zelle-new-title"
      PaperProps={{ sx: { p: 1.5, maxHeight: '90vh' } }}
    >
      <DialogTitle id="zelle-new-title" sx={{ fontWeight: 800, fontSize: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
        <Box component="span">Pay with Zelle</Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            onClick={() => { cancelSpeech(); setTimeout(runNarration, 50); }}
            startIcon={<VolumeUpIcon />}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Play Instructions
          </Button>
          <IconButton
            aria-label="close"
            onClick={() => { cancelSpeech(); onCancel && onCancel(); }}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: '#ff4d4f',
              color: 'white',
              '&:hover': { bgcolor: '#e53935' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

      </DialogTitle>
      <DialogContent sx={{ overflow: 'visible', p: 2 }}>
        <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 1, bgcolor: 'rgba(244, 67, 54, 0.08)', border: '1px solid', borderColor: 'rgba(244, 67, 54, 0.35)' }}>
          <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 800 }}>
            Attention: You must complete all 3 steps correctly.
          </Typography>
        </Box>
        <Divider sx={{ my: 2 }} />
        {/* 3x2 table layout */}
        <Table size="medium" sx={{ '& td, & th': { border: 0 }, mb: 1 }}>
          <TableBody sx={{
            '& .MuiTableRow-root': {
              '& td': { py: 3 }
            },
            // Alternate row backgrounds with Zelle-themed tints
            '& .MuiTableRow-root:nth-of-type(odd) td': {
              backgroundColor: 'rgba(111, 46, 187, 0.05)'
            },
            '& .MuiTableRow-root:nth-of-type(even) td': {
              backgroundColor: 'rgba(111, 46, 187, 0.02)'
            }
          }}>
            {/* Row 1: Scan QR */}
            <TableRow hover selected={activeStep === 1} sx={activeStep === 1 ? { outline: '2px solid #6f2ebb', outlineOffset: -2 } : undefined}>
              <TableCell sx={{ width: { md: '70%' }, pr: { md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: activeStep === 1 ? '#6f2ebb' : 'inherit' }}>Open your bank app and scan this QR</Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ width: { md: '30%' }, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box
                    component="img"
                    src={qrSrc || (process.env.PUBLIC_URL + '/assets/images/zelle.jpg')}
                    alt="Zelle QR"
                    sx={{
                      width: 240,
                      height: 240,
                      objectFit: 'contain',
                      borderRadius: 1,
                      transition: 'box-shadow 200ms, border-color 200ms',
                      border: activeStep === 1 ? '3px solid #d32f2f' : '3px solid transparent',
                      boxShadow: activeStep === 1 ? '0 0 0 4px rgba(211,47,47,0.25)' : 'none'
                    }}
                  />
                  <Typography variant="body2" sx={{ mt: 1.25, color: 'text.secondary' }}>
                    Or send to Zelle email: <strong>{zelleAddress || 'icct@icctmemphis.org'}</strong>
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>

            {/* Row 2: Enter Amount */}
            <TableRow hover selected={activeStep === 2} sx={activeStep === 2 ? { outline: '2px solid #6f2ebb', outlineOffset: -2 } : undefined}>
              <TableCell sx={{ pr: { md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>2</Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: activeStep === 2 ? '#6f2ebb' : 'inherit' }}>Enter the exact amount</Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ width: { md: '30%' }, textAlign: 'center' }}>
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: activeStep === 2 ? '3px solid #d32f2f' : '3px solid transparent',
                  boxShadow: activeStep === 2 ? '0 0 0 4px rgba(211,47,47,0.25)' : 'none',
                  borderRadius: 1,
                  transition: 'box-shadow 200ms, border-color 200ms',
                  px: 1
                }}>
                  <Typography variant="h2" sx={{ fontWeight: 900, lineHeight: 1, letterSpacing: 1 }}>
                    {prettyAmount}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>

            {/* Row 3: Memo Code */}
            <TableRow
              hover
              selected={activeStep === 3}
              sx={{
                backgroundColor: 'rgba(255, 193, 7, 0.06)',
              }}
            >
              <TableCell
                sx={{
                  pr: { md: 3 },
                  position: 'relative',
                  '@keyframes pulseAmber': {
                    '0%': { boxShadow: 'inset 0 0 0 0 rgba(255,167,38,0.0)' },
                    '50%': { boxShadow: 'inset 0 0 0 10px rgba(255,167,38,0.18)' },
                    '100%': { boxShadow: 'inset 0 0 0 0 rgba(255,167,38,0.0)' },
                  },
                  animation: 'pulseAmber 2.2s ease-in-out infinite',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>3</Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#e65100' }}>Enter this code in memo/note</Typography>
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  width: { md: '30%' },
                  textAlign: 'center',
                  position: 'relative',
                  '@keyframes pulseAmberCell': {
                    '0%': { boxShadow: 'inset 0 0 0 0 rgba(255,167,38,0.0)' },
                    '50%': { boxShadow: 'inset 0 0 0 10px rgba(255,167,38,0.18)' },
                    '100%': { boxShadow: 'inset 0 0 0 0 rgba(255,167,38,0.0)' },
                  },
                  animation: 'pulseAmberCell 2.2s ease-in-out infinite',
                  borderRadius: 1,
                }}
              >
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '3px solid #ffa726',
                  boxShadow: '0 0 0 4px rgba(255,167,38,0.25)',
                  borderRadius: 1,
                  transition: 'box-shadow 200ms, border-color 200ms',
                  px: 1,
                }}>
                  <Typography variant="h2" sx={{ fontWeight: 900, lineHeight: 1, letterSpacing: 2, color: '#e65100' }}>
                    {refCode}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {(warning || isGrace) && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Alert
              severity="warning"
              sx={{
                alignItems: 'center',
                width: '100%',
                maxWidth: 760,
                '& .MuiAlert-message': { width: '100%' }
              }}
              action={typeof onExtend === 'function' && canExtend ? (
                <Button color={'warning'} size="medium" onClick={() => { onExtend && onExtend(); }} variant="contained" sx={{ fontWeight: 800 }}>
                  Extend
                </Button>
              ) : null}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                  Session will expire soon
                </Typography>
                {typeof remainingMs === 'number' && remainingMs > 0 ? (
                  <>
                    <Typography variant="h6" display="block" sx={{ mt: 0.5, fontWeight: 800 }}>
                      Time remaining: {fmtTime(remainingMs)}
                    </Typography>
                    {remainingMs > 0 && (
                      <Typography variant="body1" display="block" sx={{ mt: 0.75, fontWeight: 700 }}>
                        {canExtend
                          ? 'Please send your payment now, or tap Extend for one final 5-minute extension.'
                          : 'Please send your payment now. No more extensions are available.'}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {canExtend ? 'Tap Extend if you need more time.' : 'No more extensions are available.'}
                  </Typography>
                )}
              </Box>
            </Alert>
          </Box>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }} action={
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {typeof onTryDifferentMethod === 'function' && (
                <Button color="inherit" size="small" variant="outlined" onClick={onTryDifferentMethod}>
                  Try different method
                </Button>
              )}
              {typeof onRetryZelle === 'function' && (
                <Button color="inherit" size="small" variant="contained" onClick={onRetryZelle}>
                  Retry Zelle
                </Button>
              )}
            </Box>
          }>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{errorMessage}</Typography>
          </Alert>
        )}
        {/* Footer: Centered status and bottom session extension */}
        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {confirmed ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <CheckCircleIcon sx={{ color: 'success.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 900, color: 'success.main' }}>
                  Payment confirmed. Creating order…
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                Please keep this screen open while we finalize your receipt.
              </Typography>
            </Box>
          ) : waiting && !errorMessage ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <CircularProgress size={34} thickness={5} sx={{ color: '#6f2ebb' }} />
                <Typography variant="h6" sx={{ fontWeight: 900, color: '#6f2ebb' }}>
                  Checking for payment confirmation…
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                If you already sent the payment, please don’t close this dialog. It may take a few moments to verify and show the confirmation page.
              </Typography>
            </Box>
          ) : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
